/**
 * Right-side action sidebar for the Report Builder docx editor.
 *
 * Three stacked icon buttons attached to the right edge of the editor pane:
 *
 *   • Refine Wording   — enabled when the user has a non-empty selection
 *   • Original Wording — enabled when the selected text matches a snapshot
 *                        captured by a previous Refine click
 *   • Dictate          — enabled when the editor has a caret (collapsed
 *                        selection) so dictated text has somewhere to land
 *
 * We deliberately don't position bubbles or carets via DOM coordinates,
 * because docx-editor's paginated rendering places page DOMs outside the
 * ProseMirror `view.dom`, which makes `coordsAtPos` and the
 * `view.dom.contains(...)` checks unreliable for popovers. Anchoring the
 * actions to a stable spot avoids that whole class of bug.
 *
 * Edits go through plain ProseMirror transactions. To find PM positions
 * without trusting `view.state.selection` (which docx-editor doesn't keep
 * in sync), we read `editorRef.current.getSelectionInfo()` for
 * `{ paraId, before, selectedText }`, then walk the PM doc to find the
 * paragraph node with that `paraId` and compute the position by adding
 * `before.length` characters from the paragraph's start.
 */
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { DocxEditorRef } from "@eigenpal/docx-editor-react";
import { RotateCcw, Sparkles, Undo2 } from "lucide-react";
import { createPortal } from "react-dom";

import { Transcriber } from "@components/Transcriber";
import type { TranscriptionCallback } from "@components/Transcriber/hooks";
import { Button } from "@components/ui/button";

import { useRefineWording } from "@pages/ReportStudio/ReportBuilder/hooks/use-refine-wording";

import { cn } from "@utils/ui";

import {
  type Refinement,
  findRefinementToRevert,
  locateUniqueOccurrence,
  normalizeForMatch,
  recordRefinement,
} from "../utils/refinement-snapshots";
import { buildInlineFragment, findParagraphStart } from "./slots";

type SelectionInfo = {
  paraId: string | null;
  selectedText: string;
  paragraphText: string;
  before: string;
  after: string;
};

type OverlayState = {
  hasSelection: boolean;
  hasCaret: boolean;
  /** Viewport-space point where the user finished their drag-selection.
   *  We position the bubble relative to this, since docx-editor renders
   *  its own selection highlight (no `window.getSelection()` rect) and
   *  PM's `coordsAtPos` returns coords for the hidden source-of-truth
   *  DOM at `left:-9999`. */
  anchor: { x: number; y: number } | null;
  /** Visible-caret position — the anchor for the floating caret mic.
   *  Derived from `document.caretRangeFromPoint(x, y)` on the click,
   *  which is the same browser primitive used to place the caret, so
   *  the mic and caret render in lockstep. For multi-line / non-empty
   *  paragraphs the caret can sit far below the paragraph's outer top,
   *  so we read the caret rect directly rather than the paragraph rect
   *  (MDRS-91). Falls back to the paragraph rect when the caret API is
   *  unavailable (older browsers) or returns an empty rect. */
  caretAnchor: { x: number; y: number } | null;
};

const INITIAL_STATE: OverlayState = {
  hasSelection: false,
  hasCaret: false,
  anchor: null,
  caretAnchor: null,
};

type EditorOverlaysProps = {
  editorRef: RefObject<DocxEditorRef | null>;
  /**
   * Visible-DOM scope for caret/selection overlays. A click is only treated
   * as targeting this editor when its `target` is inside `scopeRef.current`.
   * docx-editor renders PM off-screen at `left:-9999`, so walking
   * `getView().dom` ancestors to find `.ep-root` is unreliable — pass the
   * wrapping container ref instead.
   */
  scopeRef: RefObject<HTMLElement | null>;
};

export const EditorOverlays = ({ editorRef, scopeRef }: EditorOverlaysProps) => {
  const [state, setState] = useState<OverlayState>(INITIAL_STATE);

  // Refine-wording undo snapshots, tracked per paragraph and matched on
  // normalised text (MDRS-165). Session-only — we can't store these in
  // docx-editor's OOXML schema without a fork. See utils/refinement-snapshots.
  const refinementsRef = useRef<Refinement[]>([]);

  const { refine, isRefining, showError, showSuccess } = useRefineWording();

  const readInfo = useCallback((): SelectionInfo | null => {
    const info = editorRef.current?.getSelectionInfo?.();
    if (!info) return null;
    return info;
  }, [editorRef]);

  // Most recent mouseup point inside the editor. Captured below by a
  // document-level listener so we have a viewport coord to anchor the
  // bubble to whenever the editor reports a new selection.
  const anchorRef = useRef<{ x: number; y: number } | null>(null);
  // Anchor for the floating caret mic — paragraph rect top-left, set
  // on click. See OverlayState.caretAnchor for the why (MDRS-91).
  const caretAnchorRef = useRef<{ x: number; y: number } | null>(null);
  // Fallback paragraph identifier + PM positions derived from the DOM
  // click target. docx-editor renders each paragraph as a
  // `.layout-paragraph` div tagged with `data-pm-start` / `data-pm-end`
  // — those are PM doc positions we can dispatch transactions to.
  const clickedParaIdRef = useRef<string | null>(null);
  const clickedPmRangeRef = useRef<{ from: number; to: number } | null>(null);
  // Whether the most recent mouseup landed inside this editor. Used as a
  // multi-editor scope gate (e.g. Preview modal mounts a second docx
  // instance). We can't rely on `document.activeElement` because docx-editor
  // doesn't render a contenteditable — focus stays on body / a tabIndex=0
  // ancestor that often isn't inside `.ep-root`.
  const isActiveEditorRef = useRef(false);
  // docx-editor's `onSelectionChange` stream is the authoritative signal for
  // whether a range selection is active. `getSelectionInfo().selectedText`
  // can lag (or stay empty for paginated range selections), so we cache the
  // latest reported state and prefer its `hasSelection` flag.
  const editorSelectionRef = useRef<{ hasSelection: boolean } | null>(null);
  // True while caret-anchored dictation is actively streaming. Set in
  // `caretDictationCallback.onFocus` (called by Transcriber when the WS +
  // mic come online) and cleared in `onEnd`. Used to gate `hide()` — we
  // must NOT unmount the mic portal mid-dictation, because that tears
  // down the Transcriber's WebSocket and AudioContext, killing the
  // session. The user wants to scroll/click around while still speaking.
  const isCaretDictationActiveRef = useRef(false);

  const recompute = useCallback(() => {
    if (!isActiveEditorRef.current) {
      setState(INITIAL_STATE);
      return;
    }

    const info = readInfo();
    const selectedTextLen = info?.selectedText.length ?? 0;
    // The browser tracks the native selection over the rendered `.layout-
    // paragraph` divs even though docx-editor doesn't always reflect it in
    // `getSelectionInfo`. This is the most reliable signal — `document
    // .selectionchange` updates it live during the user's drag.
    const winSel = typeof window !== "undefined" ? window.getSelection() : null;
    const nativeHasSelection =
      !!winSel && !winSel.isCollapsed && winSel.toString().length > 0;
    const hasSelection =
      nativeHasSelection ||
      !!editorSelectionRef.current?.hasSelection ||
      selectedTextLen > 0;
    // hasCaret: docx-editor surfaces a paraId, OR the user clicked on a
    // `.layout-paragraph` and we captured its PM range from the DOM.
    const hasCaret =
      !hasSelection && (!!info?.paraId || clickedPmRangeRef.current !== null);
    setState({
      hasSelection,
      hasCaret,
      anchor: anchorRef.current,
      caretAnchor: caretAnchorRef.current,
    });
  }, [readInfo, editorRef]);

  // Subscribe to docx-editor's selection-change stream. DOM selectionchange
  // is a belt-and-braces backup in case the editor doesn't notify for some
  // transition (e.g. collapsed-caret moves between paragraphs).
  useEffect(() => {
    const handle = () => recompute();
    // Hide all overlays — used by scroll/resize so the bubble/mic don't sit
    // over the wrong content (anchor is viewport-fixed; scrolling moves the
    // content underneath it).
    //
    // Exception: when caret dictation is in progress, leave everything
    // in place. Clearing `caretAnchorRef` here would unmount the mic
    // portal, which destroys the live Transcriber's WS + audio graph
    // and silently drops the dictation. Users expect to be able to
    // scroll the document while still speaking. The mic stays at its
    // last fixed-viewport position; once they stop dictating, the next
    // recompute or hide() will clear it normally.
    const hide = () => {
      if (isCaretDictationActiveRef.current) return;
      isActiveEditorRef.current = false;
      anchorRef.current = null;
      caretAnchorRef.current = null;
      clickedParaIdRef.current = null;
      clickedPmRangeRef.current = null;
      setState(INITIAL_STATE);
    };
    // Track where mousedown landed so mouseup can tell a click from a drag.
    // A drag means the user is selecting text — we must NOT capture a caret
    // range for the mic, otherwise the mic shows alongside the selection
    // bubble (or before docx-editor reports the selection through info).
    const mouseDownPosRef = { x: 0, y: 0, valid: false };
    const onMouseDown = (e: MouseEvent) => {
      mouseDownPosRef.x = e.clientX;
      mouseDownPosRef.y = e.clientY;
      mouseDownPosRef.valid = true;
    };
    const onMouseUp = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const downX = mouseDownPosRef.x;
      const downY = mouseDownPosRef.y;
      const hadDown = mouseDownPosRef.valid;
      mouseDownPosRef.valid = false;
      const dragDistance = hadDown
        ? Math.hypot(e.clientX - downX, e.clientY - downY)
        : 0;
      // 4px is the standard click-vs-drag threshold — below it, treat as a
      // click; above, treat as a selection drag.
      const isDrag = dragDistance > 4;

      // Skip clicks on our own floating UI — pressing the mic must not
      // mutate the captured paragraph (would unmount Transcriber mid-init).
      if (target?.closest?.("[data-overlay-portal]")) return;

      // Scope to THIS editor only (MDRS-26). docx-editor renders PM
      // off-screen at `left:-9999` separate from the visible `.ep-root`
      // tree, so `getView().dom.closest('.ep-root')` resolves to null
      // and the mic anchors to any click on the page. The wrapping div
      // ref passed in by the parent is the reliable visible scope.
      // Fail closed: if the scope ref isn't attached yet (e.g. mid-mount)
      // we drop the mic rather than let it anchor anywhere on the page.
      const scope = scopeRef.current;
      if (!scope || (target && !scope.contains(target))) {
        // Outside-editor click during active caret dictation: leave the
        // session alone (same reasoning as in `hide()` above — clearing
        // the anchor unmounts the live Transcriber and stops dictation).
        if (isCaretDictationActiveRef.current) return;
        isActiveEditorRef.current = false;
        clickedParaIdRef.current = null;
        clickedPmRangeRef.current = null;
        caretAnchorRef.current = null;
        setTimeout(handle, 0);
        return;
      }

      isActiveEditorRef.current = true;
      anchorRef.current = { x: e.clientX, y: e.clientY };

      // Drag → user is selecting. Don't capture a caret range — the mic must
      // not appear under the selection. The bubble will show once docx-editor
      // surfaces the selection through `info.selectedText`.
      if (isDrag) {
        clickedParaIdRef.current = null;
        clickedPmRangeRef.current = null;
        caretAnchorRef.current = null;
        setTimeout(handle, 0);
        setTimeout(handle, 80);
        setTimeout(handle, 250);
        return;
      }

      // Find the paragraph the click is *aimed at*. We can't trust
      // `target.closest('.layout-paragraph')` because clicks in the
      // gap between paragraphs land on a container element, not on
      // the paragraph the user is reading. Use the browser's
      // caret-at-point API to snap to the nearest text caret, then
      // walk up to its containing `.layout-paragraph`.
      const findParagraphAt = (x: number, y: number): HTMLElement | null => {
        const direct = document.elementFromPoint(x, y) as HTMLElement | null;
        const closeHit = direct?.closest?.(
          ".layout-paragraph"
        ) as HTMLElement | null;
        if (closeHit) return closeHit;
        let caretNode: Node | null = null;
        if (typeof document.caretRangeFromPoint === "function") {
          caretNode = document.caretRangeFromPoint(x, y)?.startContainer ?? null;
        } else if (
          typeof (
            document as Document & {
              caretPositionFromPoint?: (
                x: number,
                y: number
              ) => { offsetNode: Node } | null;
            }
          ).caretPositionFromPoint === "function"
        ) {
          const fn = (
            document as Document & {
              caretPositionFromPoint: (
                x: number,
                y: number
              ) => { offsetNode: Node } | null;
            }
          ).caretPositionFromPoint;
          caretNode = fn(x, y)?.offsetNode ?? null;
        }
        if (!caretNode) return null;
        const el =
          caretNode.nodeType === 1
            ? (caretNode as Element)
            : caretNode.parentElement;
        return (el?.closest(".layout-paragraph") as HTMLElement | null) ?? null;
      };

      const paraEl = findParagraphAt(e.clientX, e.clientY);

      // Read the visible caret's viewport rect at the click point, using
      // the same browser primitive that places the caret. For multi-line
      // or non-empty paragraphs the caret can sit far below the para's
      // outer top, so anchoring to paraEl.getBoundingClientRect() pinned
      // the mic to line 1 while the caret was on line N (MDRS-91 follow-
      // up). Firefox uses caretPositionFromPoint; everyone else exposes
      // caretRangeFromPoint. Returns null when neither API is available
      // or the result is an empty rect — caller falls back to the
      // paragraph rect.
      const readCaretRect = (x: number, y: number): DOMRect | null => {
        let rect: DOMRect | null = null;
        if (typeof document.caretRangeFromPoint === "function") {
          rect = document.caretRangeFromPoint(x, y)?.getBoundingClientRect() ?? null;
        } else if (
          typeof (
            document as Document & {
              caretPositionFromPoint?: (
                x: number,
                y: number
              ) => { offsetNode: Node; offset: number } | null;
            }
          ).caretPositionFromPoint === "function"
        ) {
          const fn = (
            document as Document & {
              caretPositionFromPoint: (
                x: number,
                y: number
              ) => { offsetNode: Node; offset: number } | null;
            }
          ).caretPositionFromPoint;
          const pos = fn(x, y);
          if (pos) {
            const range = document.createRange();
            range.setStart(pos.offsetNode, pos.offset);
            range.collapse(true);
            rect = range.getBoundingClientRect();
          }
        }
        if (!rect) return null;
        // Some browsers return 0/0/0/0 for collapsed ranges in empty
        // text nodes — treat that as unusable and let the caller fall
        // back to the paragraph rect.
        if (
          rect.top === 0 &&
          rect.left === 0 &&
          rect.width === 0 &&
          rect.height === 0
        ) {
          return null;
        }
        return rect;
      };

      if (paraEl) {
        const pmStart = paraEl.getAttribute("data-pm-start");
        const pmEnd = paraEl.getAttribute("data-pm-end");
        if (pmStart && pmEnd) {
          clickedParaIdRef.current = paraEl.getAttribute("data-block-id") ?? null;
          clickedPmRangeRef.current = {
            from: Number(pmStart),
            to: Number(pmEnd),
          };
          // Mic follows the visible caret. Fall back to the paragraph
          // rect when the caret API is unavailable so the mic still
          // shows in a sensible spot.
          const caretRect = readCaretRect(e.clientX, e.clientY);
          const anchorRect = caretRect ?? paraEl.getBoundingClientRect();
          caretAnchorRef.current = { x: anchorRect.left, y: anchorRect.top };
        } else {
          caretAnchorRef.current = null;
        }
      } else {
        // No paragraph under the click. Two cases land here:
        //   1. Click outside the editor entirely (handled by scope check
        //      above, so this branch in practice only sees gutters /
        //      between-pages whitespace / page chrome inside `.ep-root`).
        //   2. Click in a non-editable region of the editor (gutter,
        //      page-break decoration, margin).
        // Either way there's no editable line for dictation to insert
        // into, so drop the mic (MDRS-26). Clicks on images / table
        // cells inside a paragraph still resolve to the paragraph via
        // `target.closest('.layout-paragraph')` in findParagraphAt and
        // don't hit this branch.
        clickedParaIdRef.current = null;
        clickedPmRangeRef.current = null;
        caretAnchorRef.current = null;
        isActiveEditorRef.current = false;
      }

      setTimeout(handle, 0);
      setTimeout(handle, 80);
      setTimeout(handle, 250);
    };
    const editor = editorRef.current;
    const unsub = editor?.onSelectionChange?.((state) => {
      editorSelectionRef.current = state
        ? { hasSelection: !!state.hasSelection }
        : null;
      handle();
    });
    document.addEventListener("selectionchange", handle);
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mouseup", onMouseUp);
    // Scroll/resize: hide overlays rather than re-position them. The anchor
    // is in viewport coords, so the bubble/mic would otherwise stick to the
    // screen while the content scrolls past — pointing at the wrong text.
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    handle();
    return () => {
      unsub?.();
      document.removeEventListener("selectionchange", handle);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [editorRef, scopeRef, recompute]);

  const getView = useCallback(
    () => editorRef.current?.getEditorRef()?.getView() ?? null,
    [editorRef]
  );

  // ── Refine wording ────────────────────────────────────────────────────
  const handleRefine = useCallback(async () => {
    const info = readInfo();
    if (!info?.paraId || !info.selectedText.trim()) return;
    const view = getView();
    if (!view) return;

    const refined = await refine(info.selectedText);
    if (!refined) return;

    const paraPos = findParagraphStart(view, info.paraId);
    if (paraPos === null) {
      showError("Couldn't locate the selection in the document.");
      return;
    }

    const from = paraPos + 1 + info.before.length;
    const to = from + info.selectedText.length;

    refinementsRef.current = recordRefinement(refinementsRef.current, {
      paraId: info.paraId,
      original: info.selectedText,
      refined,
    });

    const fragment = buildInlineFragment(view.state.schema, refined);
    try {
      const tr = view.state.tr.replaceWith(from, to, fragment);
      view.dispatch(tr);
      showSuccess("Wording refined.");
    } catch (err) {
      console.error("[overlay] refine replace failed:", err);
      showError("Failed to apply refined text.");
    }
  }, [getView, readInfo, refine, showError, showSuccess]);

  // ── Original wording ──────────────────────────────────────────────────
  const handleOriginal = useCallback(() => {
    const info = readInfo();
    if (!info?.paraId) return;

    // Match on normalised text (and, if the selection lags empty, on the
    // paragraph body) so revert isn't defeated by whitespace differences or a
    // lagging first-click selection (MDRS-165).
    const match = findRefinementToRevert(refinementsRef.current, {
      paraId: info.paraId,
      selectedText: info.selectedText,
      paragraphText: info.paragraphText,
    });
    if (!match) {
      showError(
        "No matching refined text. Refine the selection first to enable undo."
      );
      return;
    }
    const view = getView();
    if (!view) return;
    const paraPos = findParagraphStart(view, info.paraId);
    if (paraPos === null) {
      showError("Couldn't locate the selection in the document.");
      return;
    }

    // Prefer the live selection's range when it exactly covers the refined text;
    // otherwise (partial or lagging/empty selection) locate the refined text in
    // the paragraph. Paragraph-text offsets map to PM positions the same way the
    // refine path computes them (paraPos + 1 + offset).
    let from: number;
    let to: number;
    const selNorm = normalizeForMatch(info.selectedText);
    if (selNorm && selNorm === normalizeForMatch(match.refined)) {
      from = paraPos + 1 + info.before.length;
      to = from + info.selectedText.length;
    } else {
      // Ambiguous (refined text absent or appearing more than once) → bail
      // rather than revert an occurrence the user didn't refine.
      const idx = locateUniqueOccurrence(info.paragraphText, match.refined);
      if (idx < 0) {
        showError("Couldn't locate the refined text to revert.");
        return;
      }
      from = paraPos + 1 + idx;
      to = from + match.refined.length;
    }

    const fragment = buildInlineFragment(view.state.schema, match.original);
    try {
      const tr = view.state.tr.replaceWith(from, to, fragment);
      view.dispatch(tr);
      refinementsRef.current = refinementsRef.current.filter((r) => r !== match);
      showSuccess("Original wording restored.");
    } catch (err) {
      console.error("[overlay] restore failed:", err);
      showError("Failed to restore original wording.");
    }
  }, [getView, readInfo, showError, showSuccess]);

  const { hasSelection, hasCaret, anchor, caretAnchor } = state;
  const canRefine = hasSelection && !isRefining;
  const canRestore = hasSelection;

  // Bubble lives in a portal so ancestor overflow / transform / contain
  // can't trap or clip a `position: fixed` child. Anchored to the point
  // where the user released the mouse to finish their drag-selection.
  const bubble =
    hasSelection && anchor
      ? createPortal(
          // Outer wrapper is `pointer-events-none` so a mousedown landing
          // anywhere in its bounding box (other than the buttons) falls
          // through to the editor — letting the user start a fresh
          // selection without first dismissing the bubble.
          <div
            role="toolbar"
            data-overlay-portal=""
            aria-label="Selection actions"
            className="pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-full"
            style={{
              top: anchor.y - 12,
              left: anchor.x,
            }}
          >
            <div
              className="pointer-events-auto flex items-center gap-1 rounded-lg border bg-background-white p-1 shadow-lg"
              onMouseDown={(e) => {
                // Preserve the selection across the click ON the buttons.
                e.preventDefault();
              }}
            >
              <Button
                size="sm"
                variant="ghost"
                className="h-8 gap-1 px-2 text-xs"
                onClick={handleOriginal}
                disabled={!canRestore}
              >
                <Undo2 className="size-3.5" />
                Original Wording
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 gap-1 px-2 text-xs text-primary hover:bg-primary-50"
                onClick={handleRefine}
                disabled={!canRefine}
              >
                {isRefining ? (
                  <RotateCcw className="size-3.5 animate-spin" />
                ) : (
                  <Sparkles className="size-3.5" />
                )}
                Refine Wording
              </Button>
            </div>
          </div>,
          document.body
        )
      : null;

  // Floating mic at the cursor — appears when the user has just clicked
  // into the editor (caret placed, no selection). Anchored to the
  // mouseup point. Dictation inserts at the END of the paragraph the
  // user clicked, using the PM position read off `data-pm-end` on the
  // rendered `.layout-paragraph` element.
  const caretInsertPosRef = useRef<number | null>(null);

  const captureCaretInsertion = () => {
    // pm-end points just AFTER the paragraph's closing token; subtract 1
    // to land at the end of the paragraph's inline content.
    const range = clickedPmRangeRef.current;
    if (range) {
      caretInsertPosRef.current = range.to - 1;
      return;
    }
    // Fallback: the DOM data-pm-* attrs weren't readable (e.g. the user
    // clicked inside a table cell whose `.layout-paragraph` ancestor we
    // couldn't find). Resolve the paragraph from docx-editor's reported
    // selection info and walk the PM doc to find its end.
    const info = readInfo();
    const view = getView();
    if (!info?.paraId || !view) {
      caretInsertPosRef.current = null;
      return;
    }
    const paraPos = findParagraphStart(view, info.paraId);
    if (paraPos === null) {
      caretInsertPosRef.current = null;
      return;
    }
    const node = view.state.doc.nodeAt(paraPos);
    caretInsertPosRef.current = node ? paraPos + node.nodeSize - 1 : null;
  };

  const caretDictationCallback: TranscriptionCallback = {
    onFocus: () => {
      // Mic + WS just came online; gate `hide()` and outside-click cleanup
      // so the user can scroll the editor / click around without tearing
      // down the live session.
      isCaretDictationActiveRef.current = true;
      captureCaretInsertion();
      editorRef.current?.focus?.();
    },
    onEnd: () => {
      isCaretDictationActiveRef.current = false;
    },
    onTextInsert: (text) => {
      if (!text) return;
      const view = getView();
      if (!view) return;
      const pos = caretInsertPosRef.current;
      if (pos === null) return;
      const fragment = buildInlineFragment(view.state.schema, text);
      try {
        const tr = view.state.tr.insert(pos, fragment);
        view.dispatch(tr);
        // Advance the insert point so chained dictations append cleanly.
        caretInsertPosRef.current = pos + fragment.size;
      } catch (err) {
        console.error("[overlay] caret dictation insert failed:", err);
      }
    },
    onPartialUpdate: () => {},
  };

  const caretMic =
    hasCaret && caretAnchor && !hasSelection
      ? createPortal(
          // Outer wrapper is `pointer-events-none` so the mic doesn't
          // block drag-selecting through its bounding box. Only the
          // button itself catches clicks — and prevents default to
          // keep editor focus through the press.
          <div
            role="presentation"
            data-overlay-portal=""
            className="pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-full"
            style={{
              // Centered above the visible caret (MDRS-91). The anchor
              // point is the caret rect's top-left (or the paragraph
              // rect top-left when the caret API is unavailable);
              // `-translate-x-1/2` puts the mic icon's center on that
              // x, and `-translate-y-full` floats it 4px above.
              top: caretAnchor.y - 4,
              left: caretAnchor.x,
            }}
          >
            <div className="pointer-events-auto rounded-full border bg-background-white shadow-md">
              {/* No onMouseDown preventDefault here — the click on the
                  mic must reach the browser as a real user gesture so
                  AudioContext / AudioWorklet can initialise. We don't
                  need to preserve any selection on this press because
                  the PM range was captured on the original click. */}
              <Transcriber
                callback={caretDictationCallback}
                iconSize="sm"
                buttonClassName="!h-8 !w-8 rounded-full hover:bg-primary-50"
              />
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {bubble}
      {caretMic}
    </>
  );
};
